import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFixedPopoverPosition } from './useFixedPopover';

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
];

/**
 * Small gear button + popover for editing a lead-capture form field's own
 * properties (type, required, placeholder, and — for dropdowns — its
 * options list). Field label is edited in place via the usual EditableText
 * mechanism; this only covers the structural bits text-editing can't.
 */
export default function EditableFieldSettings({ field, onChange, editable = false }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const pos = useFixedPopoverPosition(triggerRef, open, 'above', popoverRef);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [open]);

  if (!editable) return null;

  const setField = (patch) => onChange({ ...field, ...patch });

  const addOption = () => {
    const options = field.options || [];
    setField({ options: [...options, { value: `option-${options.length + 1}`, label: 'New option' }] });
  };
  const removeOption = (idx) => setField({ options: (field.options || []).filter((_, i) => i !== idx) });
  const updateOptionLabel = (idx, label) =>
    setField({
      options: (field.options || []).map((o, i) => (i === idx ? { ...o, label } : o)),
    });

  const popover = open ? (
    <div
      ref={popoverRef}
      className="svcpg-popover"
      style={{
        position: 'fixed',
        left: pos?.left,
        top: pos?.top,
        transform: pos?.flipUp ? 'translateY(-100%)' : undefined,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <label className="svcpg-popover__field">
        <span>Field type</span>
        <select value={field.type} onChange={(e) => setField({ type: e.target.value })}>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="svcpg-popover__field">
        <span>Placeholder</span>
        <input
          type="text"
          value={field.placeholder || ''}
          onChange={(e) => setField({ placeholder: e.target.value })}
        />
      </label>
      <label className="svcpg-popover__field svcpg-popover__field--row">
        <span>Required</span>
        <input
          type="checkbox"
          checked={!!field.required}
          onChange={(e) => setField({ required: e.target.checked })}
        />
      </label>
      {field.type === 'select' ? (
        <div className="svcpg-popover__field">
          <span>Options</span>
          {(field.options || []).map((opt, i) => (
            <div key={i} className="svcpg-field-option-row">
              <input
                type="text"
                value={opt.label}
                placeholder="Option label"
                onChange={(e) => updateOptionLabel(i, e.target.value)}
              />
              <button
                type="button"
                className="svcpg-field-option-remove"
                onClick={() => removeOption(i)}
                aria-label="Remove option"
              >
                ×
              </button>
            </div>
          ))}
          <button type="button" className="svcpg-add-item svcpg-add-item--inline" onClick={addOption}>
            + Add option
          </button>
        </div>
      ) : null}
      <div className="svcpg-popover__actions">
        <button type="button" className="svcpg-toolbar__btn svcpg-toolbar__btn--active" onClick={() => setOpen(false)}>
          Done
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="svcpg-field-settings-btn"
        aria-label="Field settings"
        title="Field settings (type, required, options)"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        ⚙
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </>
  );
}
